/**
 * Resolves the `lexicon` data registry's currently published schema id
 * (VE-24948 / U5).
 *
 * WHY THIS EXISTS AT ALL — this is the one piece of U5 most likely to be
 * "simplified" back into a bug, so the reasoning lives here rather than only in
 * a design doc.
 *
 * Runtime config carries `lexiconRegistryId`, NOT a schema id. Pinning a schema
 * id in config is the pattern this repo has removed twice:
 *
 *   VE-24546 — with a pin in place, a write that failed schema validation
 *              re-resolved the SAME stale id on retry, so a genuine schema
 *              republish left saves broken until a config redeploy.
 *   VE-25957 — removed the pin from all five shipped configs, "precisely
 *              because pinning breaks saves once a newer minor version is
 *              published".
 *
 * `config_dev.js` carries the comment "Intentionally no
 * redactionStatusesSchemaId" for the same reason. U5's schema will gain minor
 * versions the first time anyone adds a field to `LexiconEntry`, so this is not
 * a theoretical risk here.
 *
 * WHY IT LIVES IN THIS MODULE. The equivalent helpers for redaction codes,
 * statuses and TDO locks sit in `@cbsa-modules/universal`, whose own first line
 * is "TODO this needs to be moved out of cbsa!". `cbsa-app` is deprecated,
 * undeployed and undeletable precisely because of imports like that one, so a
 * sixth consumer would enlarge the eventual extraction for no benefit today.
 * The extraction is raised separately.
 */

import { GraphQLExecutor, isSchemaValidationError } from './executor';
import {
  RESOLVE_LEXICON_SCHEMA_ID,
  ResolveLexiconSchemaIdResponse,
} from './queries';

/**
 * Session cache of registry id -> published schema id.
 *
 * Module-scoped rather than in Redux state: it is a transport detail with no UI
 * meaning, and holding it in the store would invite it into a persisted or
 * time-travelled snapshot, where a stale id is exactly the thing we are trying
 * to avoid.
 */
const cache = new Map<string, string>();

/** Clears the cache so the next resolve goes back to the registry. */
export function clearLexiconSchemaCache(): void {
  cache.clear();
}

/**
 * Resolves the published schema id for a lexicon registry, or `null`.
 *
 * `null` IS THE FEATURE-OFF SIGNAL, AND IT IS NOT AN ERROR (NFR-U5-12). It is
 * returned for all three ways an environment can fail to have lexicons:
 *
 *   - no `dataRegistryId` at all;
 *   - a registry id that resolves to nothing, because the registry was never
 *     provisioned in this environment;
 *   - a registry that exists with no published schema — the bootstrap ran
 *     steps 1 and 2 of the provisioning guide but not step 3.
 *
 * The third used to throw `LexiconSchemaUnpublishedError`, on the reasoning
 * that a half-provisioned registry is an operator mistake worth surfacing.
 * That distinction cost more than it bought: the caller cannot act on it, the
 * user sees a page-level error for a feature they never enabled, and it made
 * "unprovisioned environment" — the ordinary state of every environment before
 * its bootstrap runs — indistinguishable from a broken one. An environment
 * without lexicons hides lexicons. The runbook is `docs/schemas/LexiconSchema.md`.
 */
export async function resolveLexiconSchemaId(
  exec: GraphQLExecutor,
  dataRegistryId: string | undefined,
  options: { readonly ignoreCache?: boolean } = {}
): Promise<string | null> {
  if (!dataRegistryId) {
    return null;
  }

  if (!options.ignoreCache) {
    const cached = cache.get(dataRegistryId);
    if (cached) {
      return cached;
    }
  }

  const response = await exec<ResolveLexiconSchemaIdResponse>({
    query: RESOLVE_LEXICON_SCHEMA_ID,
    variables: { dataRegistryId },
    operationName: 'resolveLexiconSchemaId',
  });

  const schemaId = response?.dataRegistry?.publishedSchema?.id;
  if (!schemaId) {
    // Registry absent, or present with nothing published. Either way there is
    // no schema to write against, so the feature is off here — see above.
    return null;
  }

  cache.set(dataRegistryId, schemaId);
  return schemaId;
}

/**
 * Runs `operation` with a resolved schema id, retrying ONCE against a freshly
 * resolved id if the first attempt failed schema validation.
 *
 * This is the VE-24546 recovery: a validation failure is the symptom of holding
 * a schema id that was current when we cached it and has since been superseded
 * by a republish. Clearing the cache and re-resolving fixes it without a
 * redeploy — which is the entire payoff of not pinning the id.
 *
 * The retry is deliberately capped at one. If the second attempt also fails
 * validation, the data really is invalid against the current schema and
 * retrying further would just delay a real error.
 */
export async function withLexiconSchemaId<T>(
  exec: GraphQLExecutor,
  dataRegistryId: string | undefined,
  operation: (schemaId: string) => Promise<T>
): Promise<T> {
  const schemaId = await resolveLexiconSchemaId(exec, dataRegistryId);
  if (!schemaId) {
    throw new Error(
      'Lexicons are not configured in this environment (no lexiconRegistryId).'
    );
  }
  try {
    return await operation(schemaId);
  } catch (error) {
    if (!isSchemaValidationError(error)) {
      throw error;
    }
    clearLexiconSchemaCache();
    const refreshed = await resolveLexiconSchemaId(exec, dataRegistryId, {
      ignoreCache: true,
    });
    if (!refreshed || refreshed === schemaId) {
      // Same id back: the republish theory is wrong and the data is genuinely
      // invalid. Surface the original error rather than a confusing second one.
      throw error;
    }
    return await operation(refreshed);
  }
}
